import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

// ProjectContext.jsx is plain JS, so this import is implicitly typed `any`.
import { useProjects } from "@/contexts/ProjectContext";
import {
  blueprintsApi,
  type ListBlueprintsParams,
} from "@/services/blueprints-service";

export const BLUEPRINTS_QUERY_KEY = ["blueprints"] as const;

const DEFAULT_PAGE = 0;
const DEFAULT_PAGE_SIZE = 10;

export const useBlueprintsTableData = (params: ListBlueprintsParams = {}) => {
  const page = params.page ?? DEFAULT_PAGE;
  const pageSize = params.pageSize ?? DEFAULT_PAGE_SIZE;
  const search = params.search ?? null;

  // Blueprints are scoped server-side by the X-Project-ID header (attached
  // automatically from the active project), so the selection has to be part
  // of the cache key too — otherwise switching projects keeps showing the
  // previous project's blueprints until the entry goes stale.
  const { selectedProjects } = useProjects();
  const projectScope = useMemo<string[]>(() => {
    // Callers can override the active project selection with explicit ids.
    if (params.projectIds) return params.projectIds;

    const selection: string[] = Array.isArray(selectedProjects)
      ? selectedProjects
      : [];
    return selection.includes("all") ? [] : selection;
  }, [params.projectIds, selectedProjects]);

  const query = useQuery({
    queryKey: [...BLUEPRINTS_QUERY_KEY, search, page, pageSize, projectScope],
    queryFn: () =>
      blueprintsApi.listBlueprints({
        search,
        page,
        pageSize,
        projectIds: projectScope,
      }),
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey?.[4] === projectScope ? previous : undefined,
  });

  return {
    blueprints: query.data?.items ?? [],
    metadata: query.data?.metadata,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error as Error | null,
    refetch: query.refetch,
  };
};
